/**
 * The trader's balance in the SPA (ADR-0050): API shapes and queries. A top-up (or a fee paid in crypto) is an order
 * the payment screen (CryptoPay) shows like any crypto order.
 */
import { get } from "../../lib/api";
import type { Order } from "./Orders";
import type { OrderCrypto } from "./rails";

export type BalanceMove = {
  id: string;
  kind: "topup" | "purchase" | "refund" | "adjustment" | "overpay" | "bonus" | "bonus_expired" | "withdrawal" | "withdrawal_released";
  // ADR-0054: money (withdrawable) or bonus (a gift: spendable only, burns on its date)
  pot: "money" | "bonus";
  amount: string;
  at: string;
  orderId: string | null;
  program: string | null;
};
export type FeeRef = { kind: "reset" | "activation" | "bonus_extra" | "bonus_reset"; accountId?: string; entitlementId?: string };
export type Topup = {
  id: string;
  kind: "topup" | "fee";
  fee: (FeeRef & { result?: string }) | null;
  status: string;
  total: string;
  credited: string;
  crypto: OrderCrypto | null;
  expiresAt: string | null;
  createdAt: string;
  paidAt: string | null;
};
/** What is left of one gift and when it burns (ADR-0054). */
export type BonusGrant = { id: string; amount: string; left: string; creditedAt: string; expiresAt: string };
export type Withdrawal = {
  id: string;
  kind: "withdrawal" | "restricted_refund";
  amount: string;
  fee: string;
  net: string;
  status: "requested" | "prepared" | "paid" | "rejected";
  chain: string | null;
  address: string | null;
  reason: string | null;
  txHash: string | null;
  createdAt: string;
  decidedAt: string | null;
};
export type WithdrawTerms = { min: string; fees: { tron: string; ethereum: string }; kyc: string | null; restricted: "sanctioned" | "us" | null; list: Withdrawal[] };
export type Balance = {
  /** what can be spent: money + bonus */
  balance: string;
  money: string;
  bonus: string;
  bonusGrants: BonusGrant[];
  frozen: boolean;
  withdraw: WithdrawTerms;
  currency: string;
  limits: { min: string; max: string; presets: string[] };
  live: boolean;
  moves: BalanceMove[];
  topups: Topup[];
};

export const balanceQuery = { queryKey: ["balance"], queryFn: () => get<Balance>("/balance"), staleTime: 15_000 };

export const topupQuery = (id: string) => ({
  queryKey: ["topup", id],
  queryFn: () => get<Topup & { balance: string }>(`/balance/topups/${id}`),
  // Polled until it is settled: credited (and, for a fee, its action decided), closed or in review.
  refetchInterval: (q: { state: { data?: Topup } }) => {
    const d = q.state.data;
    return d && d.status !== "awaiting_payment" && (d.kind === "topup" || d.status !== "paid" || d.fee?.result) ? false : 4000;
  },
});

/** A top-up as the order CryptoPay reads (it needs only the payment, the id and the times). */
export const asOrder = (t: Topup): Order => ({
  id: t.id,
  program: "",
  status: t.status,
  method: "crypto",
  total: t.total,
  subtotal: t.total,
  discount: "0",
  listPrice: null,
  launchPricing: false,
  invoice: null,
  accountId: null,
  crypto: t.crypto,
  expiresAt: t.expiresAt,
  createdAt: t.createdAt,
});

/** Cents of a decimal string, exactly (no float): "12.5" → 1250n. */
export function centsOf(v: string): bigint {
  const neg = v.startsWith("-");
  const [i, f = ""] = (neg ? v.slice(1) : v).split(".");
  const c = BigInt(i || "0") * 100n + BigInt((f + "00").slice(0, 2));
  return neg ? -c : c;
}
export const decimal = (c: bigint) => `${c < 0n ? "-" : ""}${(c < 0n ? -c : c) / 100n}.${String((c < 0n ? -c : c) % 100n).padStart(2, "0")}`;
